import { useEffect, useState, useCallback, useRef } from 'react'
import { router } from '@inertiajs/react'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import { FileText, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

interface SearchResult {
  id: string
  title: string
  slug: string
  directory: string
  tags: string[]
  headline: string
  updated_at: string
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(prev => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const fetchResults = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    fetch(`/search.json?q=${encodeURIComponent(searchQuery)}`, {
      headers: { 'Accept': 'application/json' },
    })
      .then(res => res.json())
      .then(data => {
        setResults(data.results || [])
        setLoading(false)
      })
      .catch(() => {
        setResults([])
        setLoading(false)
      })
  }, [])

  const handleValueChange = useCallback((value: string) => {
    setQuery(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchResults(value), 200)
  }, [fetchResults])

  const handleSelect = useCallback((slug: string) => {
    setOpen(false)
    setQuery('')
    setResults([])
    router.visit(`/notes/${slug}`)
  }, [])

  return (
    <CommandDialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          setQuery('')
          setResults([])
        }
      }}
      title="Search Pages"
      description="Search for pages by title or content"
      showCloseButton={false}
    >
      <CommandInput
        placeholder="Search pages..."
        value={query}
        onValueChange={handleValueChange}
      />
      <CommandList>
        {query.trim() && !loading && results.length === 0 && (
          <CommandEmpty>No pages found.</CommandEmpty>
        )}
        {results.length > 0 && (
          <CommandGroup heading="Pages">
            {results.map(result => (
              <CommandItem
                key={result.id}
                value={`${result.title} ${result.slug}`}
                onSelect={() => handleSelect(result.slug)}
                className="cursor-pointer"
              >
                <FileText className="mr-2 h-4 w-4 text-gray-400" />
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="font-medium truncate">{result.title}</span>
                  <span className="text-xs text-gray-400 truncate">{result.directory}/{result.slug}</span>
                </div>
                {result.tags.slice(0, 2).map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs ml-1">
                    {tag}
                  </Badge>
                ))}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {query.trim() && results.length > 0 && (
          <CommandGroup>
            <CommandItem
              onSelect={() => {
                setOpen(false)
                router.visit(`/search?q=${encodeURIComponent(query)}`)
                setQuery('')
                setResults([])
              }}
              className="cursor-pointer"
            >
              <Search className="mr-2 h-4 w-4 text-gray-400" />
              <span>View all results for "{query}"</span>
            </CommandItem>
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
